/**
 * Iter XX.59 — VoiceComposer.
 *
 * Sits immediately above the existing text composer. Never floats in a
 * corner. Never replaces the composer.
 *
 * UX (approved in Iter XX.59 spec):
 *   Ready  → tap mic
 *   Listening (timer, "Cancel" + "Stop")
 *   Transcribing…
 *   Transcript rendered into the composer via `onTranscript(text)`
 *     → player reviews / edits / sends via existing send button
 *
 * TTS autoplay is enabled ONLY after the player uses the mic once in
 * this session (parent component owns that flag and toggles autoplay
 * on the assistant reply audio element).
 *
 * Analytics events (via /plus/track):
 *   voice_button_viewed | voice_recording_started
 *   voice_recording_cancelled | voice_recording_completed
 *   voice_transcription_succeeded | voice_transcription_failed
 *   voice_muted
 *
 * First-use label: "Too tired to type? Talk to Gully" — hidden once
 * localStorage marks the mic as discovered.
 */
import React, { useEffect, useRef, useState } from "react";
import { Mic, MicOff, Square, X, VolumeX, Volume2, Loader2 } from "lucide-react";
import api from "../lib/api";
import {
  isVoiceCapable, createRecorder, trackVoice,
} from "../lib/voice";

const _DISCOVERED_KEY = "gully_voice_discovered";
const _MUTED_KEY = "gully_voice_muted";
const MAX_SECONDS = 180;

function readMutedPreference() {
  try { return localStorage.getItem(_MUTED_KEY) === "1"; }
  catch { return false; }
}

export default function VoiceComposer({
  onTranscript,
  onMuteChange,
  onTypeInstead,       // parent focuses the text composer + hides the mic error
  ttsStatus,
  onStopPlayback,
}) {
  const [enabled, setEnabled] = useState(false);   // server flag
  const [supported, setSupported] = useState(false);
  const [state, setState] = useState("ready");     // ready|listening|transcribing|error
  const [elapsed, setElapsed] = useState(0);        // seconds
  const [discovered, setDiscovered] = useState(true);
  const [muted, setMuted] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const recorderRef = useRef(null);
  const startedAtRef = useRef(0);
  const tickRef = useRef(null);
  const viewedFiredRef = useRef(false);

  // ---- init: check capability + server flag + discovered/muted -----
  useEffect(() => {
    const cap = isVoiceCapable();
    setSupported(cap);
    if (!cap) return;
    (async () => {
      try {
        const { data } = await api.get("/voice/config");
        setEnabled(!!data?.enabled);
        if (data?.mute_by_default) setMuted(true);
      } catch { setEnabled(false); }
    })();
    try {
      setDiscovered(localStorage.getItem(_DISCOVERED_KEY) === "1");
      setMuted(readMutedPreference());
    } catch { /* noop */ }
  }, []);

  // Fire voice_button_viewed exactly once when the mic is actually
  // rendered for this player.
  useEffect(() => {
    if (viewedFiredRef.current) return;
    if (!supported || !enabled) return;
    viewedFiredRef.current = true;
    trackVoice("voice_button_viewed", { supported: true });
  }, [supported, enabled]);

  // Recording timer -------------------------------------------------
  useEffect(() => {
    if (state !== "listening") {
      if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
      return;
    }
    setElapsed(0);
    startedAtRef.current = performance.now();
    tickRef.current = setInterval(() => {
      setElapsed(Math.floor((performance.now() - startedAtRef.current) / 1000));
    }, 250);
    return () => { if (tickRef.current) clearInterval(tickRef.current); };
  }, [state]);

  if (!supported || !enabled) return null;   // hidden entirely, text chat unchanged

  const _markDiscovered = () => {
    if (discovered) return;
    setDiscovered(true);
    try { localStorage.setItem(_DISCOVERED_KEY, "1"); } catch { /* noop */ }
  };

  const _toggleMute = () => {
    const next = !muted;
    setMuted(next);
    try { localStorage.setItem(_MUTED_KEY, next ? "1" : "0"); } catch { /* noop */ }
    trackVoice("voice_muted", { muted: next });
    onMuteChange && onMuteChange(next);
  };

  const _startRecording = async () => {
    if (state !== "ready") return;
    _markDiscovered();
    setErrorMsg(null);
    const rec = createRecorder({
      maxSeconds: MAX_SECONDS,
      onStop: async ({ blob, mime, durationMs }) => {
        recorderRef.current = null;
        // Empty blob or blob too tiny (< 100 ms of audio) → treat as cancel.
        if (!blob || blob.size < 200) {
          setState("ready");
          trackVoice("voice_recording_cancelled", { reason: "empty_blob",
                                                     duration_ms: durationMs });
          return;
        }
        setState("transcribing");
        trackVoice("voice_recording_completed", { duration_ms: durationMs,
                                                    mime, bytes: blob.size });
        const t0 = performance.now();
        try {
          const fd = new FormData();
          const ext = mime.includes("mp4") ? "m4a"
                     : mime.includes("ogg") ? "ogg" : "webm";
          fd.append("file", blob, `voice.${ext}`);
          fd.append("client_recording_ms", String(durationMs));
          const { data } = await api.post("/voice/transcribe", fd, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 60000,
          });
          const roundTripMs = Math.round(performance.now() - t0);
          if (data?.ok && data.text) {
            trackVoice("voice_transcription_succeeded", {
              stt_latency_ms: data.stt_latency_ms,
              round_trip_ms: roundTripMs,
              word_count: data.word_count,
              detected_language: data.detected_language || "unknown",
              duration_ms: durationMs,
            });
            // Iter XX.60.1 — Auto-send. The transcript IS the message.
            // Player never sees a composer + Send step. Failure has its
            // own inline Try Again / Type Instead buttons below.
            onTranscript && onTranscript(data.text, {
              stt_latency_ms: data.stt_latency_ms,
              recording_ms: durationMs,
              detected_language: data.detected_language,
              word_count: data.word_count,
              auto_send: true,
            });
          } else {
            trackVoice("voice_transcription_failed", {
              reason: data?.reason || "unknown",
              stt_latency_ms: data?.stt_latency_ms,
              round_trip_ms: roundTripMs,
            });
            setErrorMsg("I couldn't quite catch that. Want to try again?");
          }
        } catch (err) {
          trackVoice("voice_transcription_failed", {
            reason: err?.response?.status ? `http_${err.response.status}` : "network",
          });
          setErrorMsg("I couldn't quite catch that. Want to try again?");
        } finally {
          setState("ready");
        }
      },
    });
    try {
      await rec.start();
      recorderRef.current = rec;
      setState("listening");
      trackVoice("voice_recording_started", {});
    } catch (err) {
      const code = err?.code || "unknown";
      if (code === "permission_denied") {
        setErrorMsg("Mic permission is off. You can still type.");
      } else if (code === "no_microphone") {
        setErrorMsg("No microphone found. You can still type.");
      } else {
        setErrorMsg("Couldn't start the mic. You can still type.");
      }
      trackVoice("voice_transcription_failed", { reason: code, stage: "start" });
    }
  };

  const _stopRecording = () => {
    if (state !== "listening") return;
    recorderRef.current && recorderRef.current.stop();
  };

  const _cancelRecording = () => {
    if (state !== "listening") return;
    trackVoice("voice_recording_cancelled", { reason: "user",
                                                duration_ms: Math.round(performance.now() - startedAtRef.current) });
    recorderRef.current && recorderRef.current.cancel();
    setState("ready");
  };

  // Render ----------------------------------------------------------
  return (
    <div className="w-full flex items-center gap-3 px-1 py-2"
         data-testid="voice-composer">
      {state === "ready" && (
        <>
          <button
            type="button"
            onClick={_startRecording}
            data-testid="voice-mic-button"
            aria-label="Start voice message"
            className="inline-flex items-center gap-2 rounded-full border border-emerald-800/60 bg-neutral-950 hover:bg-neutral-900 hover:border-emerald-700 text-emerald-300 px-3 py-1.5 transition-colors"
          >
            <Mic size={16} />
            {!discovered && (
              <span className="text-xs text-neutral-300" data-testid="voice-first-use-label">
                Too tired to type? Talk to Gully
              </span>
            )}
          </button>
          {ttsStatus === "playing" ? (
            <button
              type="button"
              onClick={onStopPlayback}
              data-testid="voice-stop-playback"
              className="inline-flex items-center gap-1.5 rounded-full border border-neutral-800 bg-neutral-950 hover:bg-neutral-900 text-neutral-300 text-xs px-2.5 py-1 transition-colors"
            >
              <Square size={12} /> Gully is speaking · stop
            </button>
          ) : (
            <button
              type="button"
              onClick={_toggleMute}
              data-testid="voice-mute-toggle"
              aria-label={muted ? "Un-mute voice replies" : "Mute voice replies"}
              className="inline-flex items-center gap-1.5 rounded-full border border-neutral-800 bg-neutral-950 hover:bg-neutral-900 text-neutral-400 text-xs px-2.5 py-1 transition-colors"
            >
              {muted ? <VolumeX size={12} /> : <Volume2 size={12} />}
              {muted ? "Voice muted" : "Voice on"}
            </button>
          )}
        </>
      )}

      {state === "listening" && (
        <>
          <span className="inline-flex items-center gap-2 rounded-full bg-emerald-900/40 border border-emerald-800 text-emerald-200 px-3 py-1.5 text-sm"
                data-testid="voice-listening">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            Listening… <span className="tabular-nums" data-testid="voice-timer">{formatSec(elapsed)}</span>
          </span>
          <button
            type="button"
            onClick={_stopRecording}
            data-testid="voice-stop-button"
            className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-1.5 text-sm font-medium transition-colors"
          >
            <Square size={14} /> Stop
          </button>
          <button
            type="button"
            onClick={_cancelRecording}
            data-testid="voice-cancel-button"
            className="inline-flex items-center gap-1.5 rounded-full border border-neutral-800 bg-neutral-950 hover:bg-neutral-900 text-neutral-400 text-sm px-3 py-1.5 transition-colors"
          >
            <X size={14} /> Cancel
          </button>
        </>
      )}

      {state === "transcribing" && (
        <span className="inline-flex items-center gap-2 rounded-full bg-neutral-900 border border-neutral-800 text-neutral-300 px-3 py-1.5 text-sm"
              data-testid="voice-transcribing">
          <Loader2 size={14} className="animate-spin" />
          Transcribing…
        </span>
      )}

      {errorMsg && state === "ready" && (
        <div className="w-full flex flex-wrap items-center gap-2"
             data-testid="voice-error">
          <span className="text-xs text-neutral-300 flex items-center gap-1.5">
            <MicOff size={12} className="text-red-400" /> {errorMsg}
          </span>
          <button
            type="button"
            onClick={() => { setErrorMsg(null); _startRecording(); }}
            data-testid="voice-try-again"
            className="text-xs rounded-full bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-1 transition-colors"
          >
            Try Again
          </button>
          <button
            type="button"
            onClick={() => {
              setErrorMsg(null);
              trackVoice("voice_fallback_to_text", {});
              onTypeInstead && onTypeInstead();
            }}
            data-testid="voice-type-instead"
            className="text-xs rounded-full border border-neutral-800 bg-neutral-950 hover:bg-neutral-900 text-neutral-300 px-3 py-1 transition-colors"
          >
            Type Instead
          </button>
        </div>
      )}

      {state === "listening" && elapsed >= MAX_SECONDS - 10 && (
        <span className="text-xs text-amber-300 ml-1" data-testid="voice-limit-warning">
          {MAX_SECONDS - elapsed}s left
        </span>
      )}
    </div>
  );
}

function formatSec(s) {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r < 10 ? "0" : ""}${r}`;
}
